import { Stack } from '@mui/material'
import { isAxiosError } from 'axios'
import React, { useCallback, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

import { LoadingButton } from 'components/common/LoadingButton'
import { TestEvaluationResultsStatusCheckIntegration } from 'components/services/create/TestEvaluationResultsStatusCheckIntegration'
import { ResponseAlertNotification } from 'components/status-checks/common/ResponseAlertNotification'
import { ResponseBodyField } from 'components/status-checks/common/ResponseBodyField'
import { ResponseCodeField } from 'components/status-checks/common/ResponseCodeField'
import { useValidateAllConfigurations } from 'components/status-checks/hooks/use-validate-all-configurations'
import {
  type StatusCheckFormValues,
  type StatusCheckFormEvaluationConfiguration
} from 'components/status-checks/status-checks-form-utils'
import { getIsStandardEndpointResponse } from 'components/status-checks/status-checks-form-validation-utils'
import {
  type StatusCheckDetailsContextValues,
  useStatusCheckDetailsContext
} from 'context/status-checks/StatusCheckDetailsContext'
import {
  type EndpointConfiguration,
  type TeamExternalIntegration
} from 'types/api/status-checks/status-check'

const TestStatusCheck: React.FC<{
  disabled: boolean
  integration: TeamExternalIntegration
  endpointConfiguration?: EndpointConfiguration
  evaluationConfiguration: StatusCheckFormEvaluationConfiguration
  onSuccess: StatusCheckDetailsContextValues['handleSuccess']
}> = ({
  disabled,
  integration,
  endpointConfiguration,
  evaluationConfiguration,
  onSuccess
}) => {
  const {
    endpointResponse,
    evaluationResults,
    resetEndpointEvaluationResponse,
    setEndpointResponse,
    setEvaluationResults
  } = useStatusCheckDetailsContext()

  const { control } = useFormContext<StatusCheckFormValues>()

  const [statusCheckMultiSelectTags] = useWatch({
    control,
    name: ['multiSelectTags']
  })

  /*
   * NOTE: Pressing the 'cancel' action can trigger the cancellation request, but we cannot know when this
   * completes from the controller instance alone. We can reset this flag once we receive a 'CancelError'
   * instance from the validation hooks through an effect implemented below.
   */
  const [isCanceling, setIsCanceling] = useState(false)

  // Response details state
  const [authResponseCode, setAuthResponseCode] = useState<number | null>(null)
  const [authResponseBody, setAuthResponseBody] = useState<string | null>(null)

  const {
    abortController,
    hasError,
    isCanceled,
    isValidating,
    isValidated,
    resetValidations,
    validateEndpointAndEvaluationConfigurations
  } = useValidateAllConfigurations({
    endpointConfiguration,
    evaluationConfiguration,
    integration,
    statusCheckMultiSelectTags
  })

  // Flags for various action button states
  const isTestActionDisabled = disabled || isValidating
  const isCancelActionDisabled = !isValidating || isCanceling

  /*
   * Flag indicating whether to show the Test Evaluation Results component. This should not apply to many
   * layout-specific integration types (e.g., Dynatrace), so we ensure the endpoint response is of the standard type.
   */
  const shouldShowTestEvaluationResults =
    isValidated &&
    evaluationResults &&
    endpointResponse &&
    getIsStandardEndpointResponse(endpointResponse)

  const resetAuthResponseDetails = useCallback(() => {
    setAuthResponseCode(null)
    setAuthResponseBody(null)
    resetEndpointEvaluationResponse()
  }, [resetEndpointEvaluationResponse])

  const handleCancelTest = () => {
    setIsCanceling(true)
    abortController.abort()
  }

  /*
   * Must go through the process of evaluating both:
   * 1. Endpoint Configuration Validation
   * 2. Evaluation Configuration Validation
   * As each step validates, context flags are updated to reflect the validation state.
   */
  const handleTestHealthCheck = async () => {
    // Clear previous response details and mutation states when initiating a new query.
    resetAuthResponseDetails()
    resetValidations()

    try {
      // First, validate the endpoint configuration. The hook handles PNI vs public integration cases internally.
      const [endpointResponse, evaluationResults] =
        await validateEndpointAndEvaluationConfigurations()

      // Update local state with the results
      setEndpointResponse(endpointResponse)
      setEvaluationResults(evaluationResults)

      /*
       * NOTE: `onSuccess` here is meant to refer to the endpoint evaluation phase.
       * Looking upstream, this sets the `externalIntegration.lastAuthenticatedResponse` field in the form context.
       */
      onSuccess(endpointResponse, evaluationResults)
    } catch (error: unknown) {
      // In the event this is not an axios error, at least throw an error toast notification to inform the user.
      if (isAxiosError(error)) {
        const { response } = error
        const { data = null, status } = response || {}
        // We can expect mainly an object or string here, but coercing to a string always for surety.
        const responseBody =
          typeof data === 'object'
            ? JSON.stringify(data, null, 2)
            : data.toString()

        setAuthResponseBody(responseBody)
        setAuthResponseCode(status ?? null)
        resetEndpointEvaluationResponse()
      }
    } finally {
      setIsCanceling(false)
    }
  }

  return (
    <Stack data-test-status-check spacing={2}>
      <Stack direction="row" spacing={2}>
        <LoadingButton
          data-testid="test-status-check"
          isDisabled={isTestActionDisabled}
          isLoading={isValidating}
          size="small"
          onClick={handleTestHealthCheck}
        >
          {isValidating ? 'Testing Health Check...' : 'Test Health Check'}
        </LoadingButton>
        <LoadingButton
          color="secondary"
          data-testid="cancel-test-status-check"
          isDisabled={isCancelActionDisabled}
          isLoading={isCanceling}
          size="small"
          variant="contained"
          onClick={handleCancelTest}
        >
          {isCanceling ? 'Canceling Test...' : 'Cancel Test'}
        </LoadingButton>
      </Stack>
      {hasError && !isCanceled && (
        <ResponseAlertNotification alertSeverity="error">
          Monitor URL returned an error or was unreachable. See response details
          below.
        </ResponseAlertNotification>
      )}
      {authResponseCode && (
        <ResponseCodeField httpStatusCode={authResponseCode} />
      )}
      {authResponseBody && (
        <ResponseBodyField responseBody={authResponseBody} />
      )}
      {shouldShowTestEvaluationResults && (
        <TestEvaluationResultsStatusCheckIntegration
          endpointResponse={endpointResponse}
          evaluationResults={evaluationResults}
          integration={integration}
        />
      )}
    </Stack>
  )
}

export default TestStatusCheck
